import { useState } from 'react';
import { Accessor, AccessorFunction } from 'react-table';

export interface UseFilterableRowsHook {
    currentRows: any[];
    setFilter: (filter: string) => void;
    filter: string;
}

export interface KeyedAccessor {
    accessor?: string | AccessorFunction<any>;
    key?: string;
}

function replaceSymbolsWithSpaces(val: string | any[]): string {
    let retVal: any = val;
    if (Array.isArray(retVal)) {
        retVal = retVal.reduce((acc: string, cur: any) => {
            let allStringProperties = Object.values(cur).reduce((acc: string, cur: any) => {
                if (typeof cur === 'string') return `${acc} ${cur}`
                return "";
            }, "");

            return `${acc} ${allStringProperties}`;
        }, "");
    }
    return String(retVal).replace(/[\W_]+/g, " ");
}

export function filterRows(rows: any[], filter: string, accessors: KeyedAccessor[], filterOn?: string[], ignoreSymbols?: boolean): any[] {
    if (!filter) return rows.slice();
    let lowerCaseFilter = filter.toLowerCase();
    if (ignoreSymbols) lowerCaseFilter = replaceSymbolsWithSpaces(lowerCaseFilter);

    let accessorHash: any = {};

    //convert key accessors to hashtable of functions
    accessors.filter(a => a && a.key && a.accessor)
        .forEach(({ key, accessor }) => {
            if (typeof accessor === 'string') {
                accessorHash[key!] = (row:any) => row[accessor];
            } else {
                accessorHash[key!] = accessor;
            }
        })

    //check if we're only filtering by certain properties
    if (filterOn && filterOn.length) {
        return rows.filter((row) => {
            if (!row) return false;
            for (let key of filterOn) {
                if (!row.hasOwnProperty(key)) continue;
                let rowValue = row[key];
                if (accessorHash[key]) {
                    rowValue = accessorHash[key](row);
                }
                if (ignoreSymbols) {
                    rowValue = replaceSymbolsWithSpaces(rowValue);
                }
                if (String(rowValue).toLowerCase().includes(lowerCaseFilter)) {
                    return true;
                }
            }
            return false;
        })
    }
    //otherwise filter every property
    else {
        return rows.filter((row) => {
            if (!row) return false;
            for (let [key, value] of Object.entries(row)) {
                let rowValue = value as any || '';
                if (accessorHash[key]) {
                    rowValue = accessorHash[key](row);
                }
                if (ignoreSymbols) {
                    rowValue = replaceSymbolsWithSpaces(rowValue);
                }
                if (String(rowValue).toLowerCase().includes(lowerCaseFilter)) {
                    return true;
                }
            }
            return false;
        });
    }
}


export function useFilterableRows(rows: any[], accessors: KeyedAccessor[], filterOn?: string[], ignoreSymbols: boolean = true): UseFilterableRowsHook {

    const [filter, setFilter] = useState("");

    const currentRows = filterRows(rows, filter, accessors, filterOn, ignoreSymbols);

    return { currentRows, setFilter, filter };
}